import { type FormEvent, type ReactNode, useEffect } from 'react';
import { t } from '../../i18n';

/**
 * The bottom sheet, written once.
 *
 * Eleven files had their own `<div className="modal-backdrop"><div
 * className="modal-panel">`, which meant eleven chances to forget the tall
 * variant, and eleven sheets that ignored the Escape key because only
 * Modal.tsx ever bothered to listen for it.
 *
 * Dismissing on a backdrop tap is opt-in, not the default: most of these
 * sheets are forms with a loan amount or a month of hours typed into them,
 * and a stray thumb on the dark area beside a phone-width panel should not
 * throw that away. The two dialogs that carry nothing (confirm, prompt) ask
 * for it explicitly.
 */
export function Sheet({
  title,
  children,
  onClose,
  onSubmit,
  tall = false,
  gap = 4,
  closeOnBackdrop = false,
  showClose = false,
  titleId,
}: {
  title?: ReactNode;
  children: ReactNode;
  onClose: () => void;
  /** Passing this renders the panel as a <form> and wires submit. */
  onSubmit?: (e: FormEvent) => void;
  /** Caps the panel at 90vh and scrolls inside it — for the long forms. */
  tall?: boolean;
  /** Vertical rhythm between children; 4 unless the sheet says otherwise. */
  gap?: 4 | 5;
  closeOnBackdrop?: boolean;
  showClose?: boolean;
  titleId?: string;
}) {
  useEscape(onClose);

  const panel = [
    'modal-panel',
    gap === 5 ? 'space-y-5' : 'space-y-4',
    tall ? 'max-h-[90vh] overflow-y-auto' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const head = title ? (
    <header className={showClose ? 'flex items-center justify-between' : undefined}>
      <h2 className="font-heading font-bold tracking-tight text-2xl" id={titleId}>
        {title}
      </h2>
      {showClose && (
        <button
          type="button"
          onClick={onClose}
          className="text-2xl text-muted-foreground hover:text-foreground leading-none px-2"
          aria-label={t('Cancel')}
        >
          ×
        </button>
      )}
    </header>
  ) : null;

  const body = (
    <>
      {head}
      {children}
    </>
  );

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the backdrop click is a redundant convenience for pointer users only — keyboard users already close this sheet with Escape via useEscape(onClose) above, and the backdrop itself is never focusable.
    <div
      className="modal-backdrop"
      // biome-ignore lint/a11y/useSemanticElements: a native <dialog> would have to be driven imperatively by showModal()/close(), which changes how every caller mounts and unmounts this sheet; role="dialog" + aria-modal announces identically and keeps the declarative render.
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={closeOnBackdrop ? (e) => e.target === e.currentTarget && onClose() : undefined}
    >
      {onSubmit ? (
        <form onSubmit={onSubmit} className={panel}>
          {body}
        </form>
      ) : (
        <div className={panel}>{body}</div>
      )}
    </div>
  );
}

/** Escape closes. Exported because ConfirmModal and PromptModal want it too. */
export function useEscape(handler: () => void) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') handler();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handler]);
}
